import { EditorMergeParams } from './editor-domain.models';
import { Size2D } from './interfaces/shared';

/**
 * Client-side replica of the backend single-page merge layout.
 *
 * The backend merger draws every image into
 * `(bounds.x + b, bounds.y + b, width + 2b, height + 2b)` where `b` is the
 * border width, and sizes the result image as the bounding box of those
 * rectangles. Request-level margin/border fields are ignored by the
 * single-page path and a per-image margin stretches the tile instead of
 * spacing it — so spacing is expressed here purely through `bounds` gaps and
 * margins are never sent.
 */

export interface MergeTileBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MergeTileLayout {
  imageIndex: number;
  bounds: MergeTileBounds;
}

export interface MergeLayoutResult {
  tiles: MergeTileLayout[];
  /** Size of the merged image exactly as the backend will compute it. */
  width: number;
  height: number;
}

export interface MergeGridShape {
  rowCount: number;
  columnCount: number;
}

/** Grows the row count so every image gets a grid cell. */
export function normalizeMergeGrid(
  rowCount: number,
  columnCount: number,
  imageCount: number,
): MergeGridShape {
  const columns = Math.max(1, Math.round(columnCount || 1));
  const rows = Math.max(1, Math.round(rowCount || 1));

  if (imageCount <= 0) {
    return { rowCount: rows, columnCount: columns };
  }

  return {
    rowCount: Math.max(rows, Math.ceil(imageCount / columns)),
    columnCount: columns,
  };
}

export function resolveMergeGridShape(
  params: Pick<EditorMergeParams, 'layout' | 'rowCount' | 'columnCount'>,
  imageCount: number,
): MergeGridShape {
  switch (params.layout) {
    case 'vertical':
      return { rowCount: Math.max(1, imageCount), columnCount: 1 };
    case 'horizontal':
      return { rowCount: 1, columnCount: Math.max(1, imageCount) };
    default:
      return normalizeMergeGrid(params.rowCount, params.columnCount, imageCount);
  }
}

export function resolveMergeLayout(
  params: EditorMergeParams,
  sizes: readonly Size2D[],
): MergeLayoutResult {
  const tiles =
    params.layout === 'custom'
      ? resolveCustomTiles(params, sizes)
      : resolveGridTiles(params, sizes);

  return {
    tiles,
    ...resolveMergedImageSize(tiles, params.borderWidth),
  };
}

/**
 * Rectangle the backend actually draws the tile into: bounds shifted and
 * inflated by the border width.
 */
export function toMergeDrawRect(bounds: MergeTileBounds, borderWidth: number): MergeTileBounds {
  return {
    x: bounds.x + borderWidth,
    y: bounds.y + borderWidth,
    width: bounds.width + borderWidth * 2,
    height: bounds.height + borderWidth * 2,
  };
}

function resolveGridTiles(
  params: EditorMergeParams,
  sizes: readonly Size2D[],
): MergeTileLayout[] {
  const { rowCount, columnCount } = resolveMergeGridShape(params, sizes.length);
  const spacing = Math.max(0, Math.round(params.spacing || 0));
  const borderWidth = Math.max(0, Math.round(params.borderWidth || 0));

  const columnWidths = new Array<number>(columnCount).fill(0);
  const rowHeights = new Array<number>(rowCount).fill(0);

  sizes.forEach((size, index) => {
    const row = Math.floor(index / columnCount);
    const column = index % columnCount;
    if (row >= rowCount) {
      return;
    }

    columnWidths[column] = Math.max(columnWidths[column], size.width);
    rowHeights[row] = Math.max(rowHeights[row], size.height);
  });

  const tiles: MergeTileLayout[] = [];
  let top = 0;

  for (let row = 0; row < rowCount; row += 1) {
    let left = 0;

    for (let column = 0; column < columnCount; column += 1) {
      const index = row * columnCount + column;
      if (index >= sizes.length) {
        return tiles;
      }

      tiles.push({
        imageIndex: index,
        bounds: {
          x: left,
          y: top,
          width: sizes[index].width,
          height: sizes[index].height,
        },
      });

      left += columnWidths[column] + borderWidth * 2 + spacing;
    }

    top += rowHeights[row] + borderWidth * 2 + spacing;
  }

  return tiles;
}

function resolveCustomTiles(
  params: EditorMergeParams,
  sizes: readonly Size2D[],
): MergeTileLayout[] {
  return params.images.slice(0, sizes.length).map((image, index) => ({
    imageIndex: index,
    bounds: {
      x: Math.max(0, Math.round(image.x || 0)),
      y: Math.max(0, Math.round(image.y || 0)),
      width: sizes[index].width,
      height: sizes[index].height,
    },
  }));
}

function resolveMergedImageSize(
  tiles: readonly MergeTileLayout[],
  borderWidth: number,
): Size2D {
  const border = Math.max(0, Math.round(borderWidth || 0));
  let width = 0;
  let height = 0;

  for (const tile of tiles) {
    const drawRect = toMergeDrawRect(tile.bounds, border);
    width = Math.max(width, Math.ceil(drawRect.x + drawRect.width));
    height = Math.max(height, Math.ceil(drawRect.y + drawRect.height));
  }

  return {
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}
